import { useMutation, useQuery } from "@apollo/client";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Breadcrumb from "../../components/Breadcrumb";
import Card from "../../components/Card";
import { useToast } from "../../components/Toast/toastHook";
import {
  EmptyState,
  ErrorBanner,
  ErrorState,
  skeletonTableProps,
} from "../../components/states";
import Modal from "../../components/modal/Modal";
import ServiceForm from "../../components/directory/ServiceForm";
import { formatDate } from "../../utils/dateFormat";
import { DELETE_SERVICE, GET_ALL_SERVICES } from "../../graphql/serviceQueries";
import { Button, Table } from "antd";
import {
  PlusOutlined as PlusIcon,
  EditOutlined as EditIcon,
  DeleteOutlined as DeleteIcon,
} from "@ant-design/icons";
import useDebounce from "../../hooks/useDebounce";
import TableToolbar from "../../components/TableToolbar/TableToolbar";
import { toServerSort } from "../../utils/tableSort";
import useConfirmDialog from "../../components/ConfirmDialog/useConfirmDialog";
import { TABLE_SCROLL } from "../../constants/table";

// Columns the server can order the services by.
const SORTABLE_COLUMNS = ["title", "createdAt", "updatedAt"];

const Services = () => {
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalCount: 0,
  });

  const [showModal, setShowModal] = useState(false);
  const [formType, setFormType] = useState("add");
  // For edit
  const [service, setService] = useState(null);

  const [serviceListing, setServiceListing] = useState([]);

  const [searchText, setSearchText] = useState("");
  const debounceValue = useDebounce(searchText, 500);
  const [sort, setSort] = useState(null);

  const toast = useToast(4000);
  const { confirm, dialog } = useConfirmDialog();
  const [deleteService] = useMutation(DELETE_SERVICE);

  const { data, loading, error, refetch } = useQuery(GET_ALL_SERVICES, {
    fetchPolicy: "no-cache",
    variables: {
      pagination: {
        page: pagination.page,
        limit: pagination.limit,
      },
      searchText: debounceValue,
      ...(sort && { sort }),
    },
  });

  const columns = [
    {
      title: "Types",
      dataIndex: "title",
      key: "title",
      sorter: true,
      render: (text, record) => (
        <Link to={`/administrator/services/${record._id}`}>
          <div className="flex items-center space-x-2 text-black">
            {record.icon && (
              <img
                src={record.icon}
                alt=""
                className="h-[30px] w-[30px] object-cover"
              />
            )}
            <span>{record.title}</span>
          </div>
        </Link>
      ),
    },
    {
      title: "Date Created",
      dataIndex: "createdAt",
      key: "createdAt",
      sorter: true,
      render: (createdAt) => (
        <span className="font-medium text-black">{formatDate(createdAt)}</span>
      ),
    },
    {
      title: "Date Updated",
      dataIndex: "updatedAt",
      key: "updatedAt",
      sorter: true,
      render: (updatedAt) => (
        <span className="font-medium text-black">{formatDate(updatedAt)}</span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <div className="flex items-center space-x-2">
          <Link
            to={`/administrator/services/${record._id}?name=${record.title}`}
          >
            <Button
              className="border-secondary text-secondary"
              icon={<PlusIcon />}
            />
          </Link>
          <Button
            className="border-primary text-primary"
            icon={<EditIcon />}
            onClick={() => handleEdit(record)}
          />

          <Button
            danger
            icon={<DeleteIcon />}
            onClick={() => handleDelete(record)}
          />
        </div>
      ),
    },
  ];

  const onChange = (nextPagination, _tableFilters, sorter, extra) => {
    // A new sort order starts again from the first page.
    if (extra?.action === "sort") {
      setSort(toServerSort(sorter, SORTABLE_COLUMNS));
      setPagination((prev) => ({ ...prev, page: 1 }));
      return;
    }
    setPagination((prev) => ({
      ...prev,
      page: nextPagination?.current,
      limit: nextPagination?.pageSize,
    }));
  };

  useEffect(() => {
    if (data?.getAllServices?.services)
      setServiceListing(data?.getAllServices?.services);
    setPagination((prev) => ({
      ...prev,
      totalCount: data?.getAllServices?.totalCount,
    }));
  }, [data]);

  useEffect(() => {
    if (!error) return;
    toast("error", error.message);
  });

  const handleEdit = (directoryToEdit) => {
    setService(directoryToEdit);
    setFormType("edit");
    setShowModal(true);
  };
  const handleModalRender = () => {
    setFormType("add");
    setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
  };

  const handleDelete = (record) =>
    confirm({
      title: `Delete ${record.title}?`,
      description:
        "This service will be permanently deleted. This action cannot be undone.",
      onConfirm: async () => {
        try {
          const { data: res } = await deleteService({
            variables: { serviceId: record._id },
          });
          toast(
            "success",
            `${res.deleteService.title} service deleted successfully.`
          );
          refetch();
        } catch (e) {
          toast("error", e.message);
        }
      },
    });

  const firstLoad = loading && !serviceListing.length;

  return (
    <div id="companies">
      <Breadcrumb pageName="Services" />
      <div className="pb-6">
        <Card>
          <TableToolbar
            title="All Services"
            count={pagination.totalCount || 0}
            searchValue={searchText}
            onSearch={setSearchText}
            action={
              <button
                className="bg-[#B4C540] h-[36px] px-5 text-center rounded-xl text-[#FFFFFF]"
                onClick={handleModalRender}
              >
                + Add service
              </button>
            }
          />
          {error && !serviceListing.length ? (
            <ErrorState onRetry={() => refetch()} />
          ) : (
            <>
              {error && <ErrorBanner onRetry={() => refetch()} />}
              <Table
                scroll={TABLE_SCROLL}
                bordered
                rowKey="_id"
                onChange={onChange}
                {...(firstLoad
                  ? skeletonTableProps(columns)
                  : {
                      columns,
                      dataSource: serviceListing,
                      pagination: {
                        position: ["bottomCenter"],
                        current: pagination.page,
                        pageSize: pagination.limit,
                        total: pagination.totalCount,
                      },
                    })}
                locale={{
                  emptyText: searchText ? (
                    <EmptyState
                      title="No services match your search"
                      description={`Nothing found for "${searchText}".`}
                      actionLabel="Clear search"
                      onAction={() => setSearchText("")}
                    />
                  ) : (
                    <EmptyState
                      title="No services yet"
                      description="Services you add will be offered to companies."
                    />
                  ),
                }}
              />
            </>
          )}
        </Card>
      </div>
      <Modal
        title={formType === "add" ? "Add Service" : "Edit Service"}
        showModal={showModal}
        setShowModal={(value) => {
          setShowModal(value);
          // if (!value) setOffice({});
        }}
      >
        <ServiceForm
          service={service}
          formType={formType}
          handleClose={handleClose}
          onServiceAdded={() => refetch()}
        />
      </Modal>
      {dialog}
      {/* <Modal
          title="Assigned Agents"
          showModal={agentsModal}
          setShowModal={(value) => {
            setAgentsModal(value);
            // setAgents([]);
          }}
        ></Modal> */}
    </div>
  );
};

export default Services;
